import { useEffect, useState } from "react"
import { Check, Copy } from "lucide-react"
import { cn } from "@/lib/utils"
import { useTranslation } from "react-i18next"

// Copies "lat, lon" at full precision: the dispatcher pastes it into Marcos or
// a map, and a rounded pair lands a few metres off the point gps will use.
export const coordinatesForClipboard = (lat: number, lon: number): string =>
  `${lat}, ${lon}`

type CopyCoordinatesButtonProps = {
  lat: number
  lon: number
  className?: string
}

export function CopyCoordinatesButton({
  lat,
  lon,
  className,
}: CopyCoordinatesButtonProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 1500)
    return () => clearTimeout(timer)
  }, [copied])

  const handleCopy = async (event: React.MouseEvent<HTMLButtonElement>) => {
    // The stop card behind is clickable (opens the editor) and draggable.
    event.stopPropagation()
    event.preventDefault()
    const text = coordinatesForClipboard(lat, lon)
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      window.appToaster?.show({
        message: t("maps.copyCoordinatesButton.copied"),
        intent: "success",
        timeout: 2000,
      })
    } catch (error) {
      console.error("Failed to copy coordinates:", error)
      window.appToaster?.show({
        message: t("maps.copyCoordinatesButton.copyFailed"),
        intent: "danger",
        timeout: 3000,
      })
    }
  }

  return (
    <button
      type="button"
      aria-label={t("maps.copyCoordinatesButton.copy")}
      title={copied ? t("common.copied") : t("maps.copyCoordinatesButton.copy")}
      data-testid="copy-coordinates"
      onClick={handleCopy}
      onMouseDown={(event) => event.stopPropagation()}
      className={cn(
        "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200",
        className,
      )}
    >
      {copied ? (
        <Check className="h-3 w-3 text-green-600" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
    </button>
  )
}
